import React, { useEffect, useState, Fragment } from "react";
import { useSudowriteStore } from "../../../stores/sudowriteStore";
import { StepSelector } from "../stepper";
import { Step } from "./step";
import { Choices } from "../choices";
import { sendAppEvent } from "../../../Utils";
import { variationName } from "../index";

const viewEventName = "survey_step_viewed";

export function ConsentStep({ onAccept, onReject }) {
  const handleNext = () => {
    onAccept();
  };
  useEffect(() => {
    sendAppEvent(viewEventName, {
      step_name: "ConsentStep",
      survey_variation: variationName,
    });
  }, []);

  return (
    <Step
      onSkip={onReject}
      onNext={handleNext}
      skipLabel="Not Now"
      nextLabel="Sure"
    >
      <header>
        <h1>Would you answer 4 (extremely) quick questions so we can make Sudowrite better for you?</h1>
      </header>
      <div className="main">
        <div className="spacer" />
      </div>
    </Step>
  );
}

export function CanIReadItStep({ onSubmit, onSkip, name, actionsDisabled, experiments }) {
  const userData = useSudowriteStore((state) => state.userData);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    sendAppEvent(viewEventName, {
      step_name: name,
      survey_variation: variationName,
      ...experiments,
    });
  }, []);

  const handleSelection = (e) => {
    setSelected(e.target.value);
  };

  const handleNext = async () => {
    if (selected === "YES") {
      await userData.update({ needsImport: true });
    }
    onSubmit(name, selected);
  };

  const options = [
    { label: "Yes! Let me import it.", value: "YES" },
    {
      label: "Maybe later. I'd like to try a demo first.",
      value: "NO",
    },
  ];

  return (
    <Step
      onSkip={onSkip}
      onNext={handleNext}
      nextDisabled={actionsDisabled || selected === null}
      skipDisabled={actionsDisabled}
      lastStep={true}
    >
      <header>
        <p className="conversation">
          I can import your manuscript or notes and organize it all for you.
        </p>
        <p className="conversation">
        (I'm most helpful when we're working together on your project.)
        </p>
      </header>
      <div className="main">
        <Choices
          name={name}
          onSelect={handleSelection}
          options={options}
          selected={selected}
          wrapped={false}
        />
        <div className="spacer" />
      </div>
    </Step>
  );
}

export function NewProjectOrDemoStep({ onSubmit, onSkip, name, actionsDisabled, experiments }) {
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    sendAppEvent(viewEventName, {
      step_name: name,
      survey_variation: variationName,
      ...experiments,
    });
  }, []);

  const handleSelection = (e) => {
    setSelected(e.target.value);
  };

  const handleNext = async () => {
    onSubmit(name, selected);
  };

  const options = [
    { label: "Start writing a story", value: "PROJECT" },
    {
      label: "Learn about Sudowrite's write and rewrite tools",
      value: "DEMO",
    },
  ];

  return (
    <Step
      onSkip={onSkip}
      onNext={handleNext}
      nextDisabled={actionsDisabled || selected === null}
      skipDisabled={actionsDisabled}
      lastStep={true}
    >
      <header>
        <p className="conversation">
          Shall we start writing a story together?
        </p>
        <p className="conversation">
          We'll begin by filling out a Story Bible. I use the Story Bible as context whenever I write, edit, give feedback, etc.
        </p>
      </header>
      <div className="main">
        <Choices
          name={name}
          onSelect={handleSelection}
          options={options}
          selected={selected}
          wrapped={false}
        />
        <div className="spacer" />
      </div>
    </Step>
  );
}

export function CurrentProjectStep({ onSubmit, onSkip, name, actionsDisabled, experiments }) {
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    sendAppEvent(viewEventName, {
      step_name: name,
      survey_variation: variationName,
      ...experiments,
    });
  }, []);

  const handleSelection = (e) => {
    setSelected(e.target.value);
  };

  const handleNext = () => {
    onSubmit(name, selected);
  };

  const options = [
    { label: "I'm just starting out", value: "NO" },
    {
      label: "I have an outline / synopsis",
      value: "YES_OUTLINE",
    },
    {
      label: "I have an outline / synopsis and have written at least a chapter",
      value: "YES_CHAPTER",
    },
    {
      label: "I have written a full draft",
      value: "YES_DRAFT",
    },
  ];

  return (
    <Step
      onSkip={onSkip}
      onNext={handleNext}
      nextDisabled={actionsDisabled || selected === null}
      skipDisabled={actionsDisabled}
    >
      <header>
        <p className="conversation">I understand what you're looking for help with.</p>
        <p className="conversation">Are you currently working on a book? If so, how far along are you?</p>
      </header>
      <div className="main">
        <Choices
          name={name}
          onSelect={handleSelection}
          options={options}
          selected={selected}
          wrapped={false}
        />
        <div className="spacer" />
      </div>
    </Step>
  );
}

export function GenreStep({ onSubmit, onSkip, name, actionsDisabled, experiments }) {
  const [selected, setSelected] = useState(new Set());
  useEffect(() => {
    sendAppEvent(viewEventName, {
      step_name: name,
      survey_variation: variationName,
      ...experiments,
    });
  }, []);
  const handleSelection = (e) => {
    if (e.target.checked) {
      const newSources = [...selected, e.target.value];
      setSelected(new Set(newSources));
    } else {
      selected.delete(e.target.value);
      setSelected(new Set(Array.from(selected)));
    }
  };

  const handleNext = () => {
    onSubmit(name, [...selected]);
  };

  const options = [
    { label: "Science Fiction", value: "SCI_FI" },
    { label: "Fantasy", value: "FANTASY" },
    { label: "Mystery", value: "MYSTERY" },
    { label: "Romance", value: "ROMANCE" },
    { label: "Thriller", value: "THRILLER" },
    { label: "Horror", value: "HORROR" },
    { label: "Historical Fiction", value: "HISTORICAL_FICTION" },
    { label: "Literary Fiction", value: "LITERARY_FICTION" },
    { label: "Non-Fiction", value: "NON_FICTION" },
    { label: "Erotica", value: "EROTICA" },
    { label: "Children's", value: "CHILDREN" },
    { label: "Young Adult", value: "YOUNG_ADULT" },
    { label: "Other", value: "OTHER" },
  ];

  return (
    <Step
      onSkip={onSkip}
      onNext={handleNext}
      nextDisabled={actionsDisabled || selected.size === 0}
      skipDisabled={actionsDisabled}
    >
      <header>
        <p className="conversation">Cool. I can't wait to read what you have so far.</p>
        <p className="conversation">What kind of story is it?</p>
      </header>
      <div className="main">
        <Choices
          multi
          name={name}
          onSelect={handleSelection}
          options={options}
          selected={selected}
          wrapped={true}
        />
        <div className="spacer" />
      </div>
    </Step>
  );
}

export function WriterExperienceStep({ onSkip, onSubmit, name, actionsDisabled, experiments }) {
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    sendAppEvent(viewEventName, {
      step_name: name,
      survey_variation: variationName,
      ...experiments,
    });
  }, []);

  const handleSelection = (e) => {
    setSelected(e.target.value);
  };

  const handleNext = () => {
    onSubmit(name, selected);
  };
  const options = [
    { label: "I'm a professional writer", value: "PROFESSIONAL" },
    { label: "I aspire to be a professional writer", value: "ASPIRING_PROFESSIONAL" },
    { label: "It's a hobby", value: "HOBBYIST" },
    { label: "I'm a student", value: "STUDENT" },
    { label: "Other", value: "OTHER" },
  ];

  return (
    <Step
      onSkip={onSkip}
      onNext={handleNext}
      nextDisabled={actionsDisabled || selected === null}
      skipDisabled={actionsDisabled}
    >
      <header>
        <p className="conversation">Hello! Let's get started.</p>
        <p className="conversation">How do you think about your writing?</p>
      </header>
      <div className="main">
        <Choices
          name={name}
          onSelect={handleSelection}
          options={options}
          selected={selected}
          wrapped={false}
        />
        <div className="spacer" />
      </div>
    </Step>
  );
}

export function EarnedMoneyStep({ onSkip, onSubmit, name, actionsDisabled, experiments }) {
  const [hasEarned, setHasEarned] = useState(null);
  const [sources, setSources] = useState(new Set());

  const [question, setQuestion] = useState(1);
  useEffect(() => {
    sendAppEvent(viewEventName, {
      step_name: name,
      survey_variation: variationName,
      ...experiments,
    });
  }, []);

  const handleSelection = (e) => {
    setHasEarned(e.target.value);
  };

  const handleNext = () => {
    if (question == 1 && hasEarned.startsWith("YES")) {
      setQuestion(2);
    } else {
      handleSubmit(hasEarned, sources);
    }
  };

  const handleSkip = () => {
    switch (question) {
      case 1:
        onSkip();
        break;
      case 2:
        handleSubmit(hasEarned);
        break;
      default:
        break;
    }
  };

  const handleSubmit = (hasEarned, sources) => {
    const data = {
      hasEarned,
    };

    if (sources) {
      data.sources = [...sources];
    }
    onSubmit(name, data);
  };

  const nextDisabled = actionsDisabled || (question === 1 ? hasEarned === null : sources.size === 0);

  const options = [
    { label: "No, I haven't and don't plan to", value: "NO_AND_NO_PLANS" },
    { label: "No, but I would like to in the future", value: "NO_BUT_IN_FUTURE" },
    { label: "Yes, once or twice", value: "YES_FEW_TIMES" },
    { label: "Yes, quite often", value: "YES_OFTEN" },
  ];

  const handleSourceSelection = (e) => {
    if (e.target.checked) {
      const newSources = [...sources, e.target.value];
      setSources(new Set(newSources));
    } else {
      sources.delete(e.target.value);
      setSources(new Set(Array.from(sources)));
    }
  };
  const earningsSources = [
    { label: "Traditional books / novels", value: "TRADITIONAL_BOOKS" },
    { label: "Kindle books", value: "KINDLE" },
    { label: "Content for clients or ghostwriting", value: "GHOSTWRITING" },
    { label: "Other freelance projects", value: "OTHER" },
  ];

  const question1 = (
    <Fragment>
      <header>
        <h1>Have you earned money from publishing writing before?</h1>
        <h2>Choose one</h2>
      </header>
      <div className="main">
        <Choices
          name="earningsSource"
          onSelect={handleSelection}
          options={options}
          selected={hasEarned}
          wrapped={false}
        />
        <div className="spacer" />
      </div>
    </Fragment>
  );

  const question2 = (
    <Fragment key={1}>
      <header>
        <h1>What kind of writing work did you earn money from?</h1>
        <h2>Select all that apply</h2>
      </header>
      <div className="main">
        <Choices
          multi
          name={name}
          onSelect={handleSourceSelection}
          options={earningsSources}
          selected={sources}
          wrapped={false}
        />
        <div className="spacer" />
      </div>
    </Fragment>
  );

  return (
    <Step
      onSkip={handleSkip}
      onNext={handleNext}
      nextDisabled={nextDisabled}
      skipDisabled={actionsDisabled}
    >
      <StepSelector
        selected={question}
        steps={[
          { id: 1, content: question1 },
          { id: 2, content: question2 },
        ]}
      />
    </Step>
  );
}

export function NeedHelpWithStep({ onSkip, onSubmit, name, actionsDisabled, experiments }) {
  const [selected, setSelected] = useState(new Set());
  useEffect(() => {
    sendAppEvent(viewEventName, {
      step_name: name,
      survey_variation: variationName,
      ...experiments,
    });
  }, []);

  const handleSelection = (e) => {
    if (e.target.checked) {
      const newSources = [...selected, e.target.value];
      setSelected(new Set(newSources));
    } else {
      selected.delete(e.target.value);
      setSelected(new Set(Array.from(selected)));
    }
  };

  const handleNext = () => {
    onSubmit(name, [...selected]);
  };

  const options = [
    { label: "Overcoming writer's block", value: "WRITERS_BLOCK" },
    { label: "Writing quickly & efficiently", value: "QUICK_AND_EFFICIENT" },
    { label: "Editing and refining my work", value: "EDITING" },
    { label: "Brainstorming ideas", value: "BRAINSTORM" },
    { label: "Plot & character development", value: "PLOT_AND_CHARACTER" },
  ];

  return (
    <Step
      onSkip={onSkip}
      onNext={handleNext}
      nextDisabled={actionsDisabled || selected.size === 0}
      skipDisabled={actionsDisabled}
    >
      <header>
        <p className="conversation">Great! I'm Sudowrite, your writing partner.</p>
        <p className="conversation">How can I help with your writing process?</p>
      </header>
      <div className="main">
        <Choices
          multi
          name={name}
          onSelect={handleSelection}
          options={options}
          selected={selected}
          wrapped={false}
        />
        <div className="spacer" />
      </div>
    </Step>
  );
}
